import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import './FeaturedProducts.css';

// Fallback used only if the DB value is missing/invalid
const DEFAULT_FEATURED_LIMIT = 3;

export default function FeaturedProducts() {
  const { data } = useData();
  const products = data?.products || [];

  // Admin-controlled limit, stored as a `company` key-value in the DB
  const rawLimit = data?.company?.featuredProductsLimit;
  const parsedLimit = parseInt(rawLimit, 10);
  const featuredLimit = Number.isFinite(parsedLimit) && parsedLimit > 0
    ? parsedLimit
    : DEFAULT_FEATURED_LIMIT;

  // Only the "main" (live) products are surfaced on the Home page
  const featured = products
    .filter(p => p.status === 'live')
    .slice(0, featuredLimit);

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [selected]);

  const handleConfirm = () => {
    if (selected?.url) {
      window.open(selected.url, '_blank', 'noopener,noreferrer');
    }
    setSelected(null);
  };

  return (
    <section className="section featured">
      <div className="container">
        <div className="section-title section-title--center">
          <span className="section-title__label">Our Ecosystem</span>
          <h2 className="section-title__heading">Featured Products</h2>
          <p className="section-title__description">
            Explore our growing suite of products spanning every major industry and technology.
          </p>
        </div>

        <div className="featured__grid">
          {featured.map(project => (
            <div
              key={project.id}
              className="featured__card featured__card--clickable"
              onClick={() => setSelected(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(project);
                }
              }}
            >
              <div className="featured__icon" style={{ backgroundColor: `${project.color}15` }}>
                <span>{project.icon}</span>
              </div>
              <div className="featured__status">
                <span className={`featured__badge featured__badge--${project.status}`}>
                  {project.status === 'live' ? 'Live' : 'In Development'}
                </span>
              </div>
              <h3 className="featured__title">{project.name}</h3>
              <p className="featured__tagline">{project.tagline}</p>
              <p className="featured__description">{project.description}</p>
              <div className="featured__tags">
                {project.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="featured__tag">{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="featured__cta">
          <Link to="/products" className="btn btn--primary">View All Products</Link>
        </div>
      </div>

      {selected && (
        <div
          className="featured-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="featured-modal-title"
          onClick={() => setSelected(null)}
        >
          <div
            className="featured-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="featured-modal__close"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="featured__icon" style={{ backgroundColor: `${selected.color}15` }}>
              <span>{selected.icon}</span>
            </div>
            <div className="featured__status">
              <span className={`featured__badge featured__badge--${selected.status}`}>
                {selected.status === 'live' ? 'Live' : 'In Development'}
              </span>
            </div>
            <h3 id="featured-modal-title" className="featured__title">{selected.name}</h3>
            <p className="featured__tagline">{selected.tagline}</p>
            <p className="featured__description">{selected.description}</p>
            <div className="featured__tags">
              {selected.tags.slice(0, 3).map(tag => (
                <span key={tag} className="featured__tag">{tag}</span>
              ))}
            </div>

            <p className="featured-modal__prompt">
              This will open <strong>{selected.name}</strong> in a new tab. Continue?
            </p>
            <div className="featured-modal__actions">
              <button className="btn btn--primary" onClick={handleConfirm}>
                Launch Product
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
